import { useEffect, useState } from 'react';
import { ladeFunktionsstand } from './client';

/**
 * Was jede Sitzung über die Einstellungen der Installation wissen muss: ob das
 * Kundenguthaben eingeschaltet ist (Auftrag `PF-guthabenschalter`, Spec
 * Systemeinstellungen 8.5, `D-57`, Fall `E-2078`) und ob die heute geltende
 * Freigaberegel vier Augen verlangt (Auftrag `W-vieraugentexte`, `D-31`, Fall
 * `E-2160`).
 *
 * **Woher.** `GET /api/v1/einstellungen/funktionen`, lesbar für jede Sitzung —
 * dieselbe Zeile `funktion_schalter` (`guthaben`), die der Server bei jedem
 * schreibenden Guthabenweg liest, und dieselbe Frage an `freigabeprofil`, die
 * jede Freigabe stellt (`einzelfreigabeErlaubt()`). Keine zweite Quelle: Die
 * Oberfläche blendet nur aus, was der Server ohnehin mit
 * `409 GUTHABEN_ABGESCHALTET` abweist, und sperrt einen Freigabeknopf nur dort,
 * wo der Server die Selbstfreigabe abweisen würde.
 *
 * **Einmal je Seitenleben geladen und geteilt**, wie die Bezeichnungen
 * (`bezeichnungen.ts`): Menüspalte, Belegansicht, Startseite, Kundenansicht und
 * jede Freigabeliste fragen dieselbe Antwort. Nach dem Umschalten setzt der
 * Schalter selbst den neuen Stand ({@link guthabenStandSetzen}); nach einer
 * neuen Freigaberegel fragt der Schritt neu ({@link funktionsstandNeuLaden}).
 *
 * **Bis zur Antwort — und wenn sie scheitert — gilt „Guthaben eingeschaltet“
 * und „vier Augen“**: der Stand jeder bestehenden Installation (`0255`) und die
 * Vorgabe der Freigaberegel (`0263`). Ein Fehlschlag soll keinen Weg der eigenen
 * Installation verstecken und keine Selbstfreigabe anbieten, die der Server
 * abweisen könnte. Die benannte Grenze: Auf einer Installation mit
 * ausgeschaltetem Guthaben kann ein Guthabenelement bis zur ersten Antwort
 * sichtbar sein, und unter Einzelfreigabe ist der eigene Freigabeknopf bis dahin
 * gesperrt.
 */
interface Stand {
  readonly guthaben: boolean;
  readonly vierAugen: boolean;
}

const VORGABE: Stand = Object.freeze({ guthaben: true, vierAugen: true });

let geladen: Stand | undefined;
let unterwegs = false;
const zuhoerer = new Set<(stand: Stand) => void>();

function melde(stand: Stand): void {
  geladen = stand;
  for (const hoeren of zuhoerer) hoeren(stand);
}

function anfragen(): void {
  if (unterwegs) return;
  unterwegs = true;
  let versprochen: unknown;
  try {
    versprochen = ladeFunktionsstand();
  } catch {
    // Eine Testattrappe ohne Vorgabe: der Bestand bleibt.
    unterwegs = false;
    return;
  }
  if (typeof (versprochen as { then?: unknown } | undefined)?.then !== 'function') {
    // Eine Testattrappe, die nichts zurückgibt: der Bestand bleibt.
    unterwegs = false;
    return;
  }
  void (versprochen as ReturnType<typeof ladeFunktionsstand>).then(
    (stand) => {
      const guthaben = stand.funktionen.find((f) => f.schluessel === 'guthaben');
      melde({ guthaben: guthaben?.aktiv ?? true, vierAugen: stand.vierAugen });
    },
    () => {
      unterwegs = false;
    },
  );
}

/** Setzt den Stand des Guthabens und meldet ihn jedem Zuhörer — nach dem Umschalten aufgerufen. */
export function guthabenStandSetzen(aktiv: boolean): void {
  melde({ ...(geladen ?? VORGABE), guthaben: aktiv });
}

/**
 * Fragt den Stand neu — nach einer neuen Freigaberegel aufgerufen. Gilt die neue
 * Fassung erst ab einem späteren Tag, bleibt die Antwort die von heute.
 */
export function funktionsstandNeuLaden(): void {
  unterwegs = false;
  anfragen();
}

/** Nur für Tests: vergisst den geladenen Stand, damit der nächste Aufruf neu fragt. */
export function funktionsstandVergessen(): void {
  geladen = undefined;
  unterwegs = false;
}

function useStand(): Stand {
  const [stand, setStand] = useState<Stand>(geladen ?? VORGABE);
  useEffect(() => {
    zuhoerer.add(setStand);
    if (geladen === undefined) anfragen();
    else setStand(geladen);
    return () => {
      zuhoerer.delete(setStand);
    };
  }, []);
  return stand;
}

/** `true`, solange das Kundenguthaben eingeschaltet ist oder der Stand noch nicht feststeht. */
export function useGuthabenAktiv(): boolean {
  return useStand().guthaben;
}

/**
 * `true`, solange die heute geltende Freigaberegel vier Augen verlangt oder der
 * Stand noch nicht feststeht; `false` unter Einzelfreigabe. Der Lesehelfer jedes
 * Freigabetextes der Oberfläche: Ein Text, der „eine zweite Person“ sagt, steht
 * in einer Datei, die ihn benutzt (`pnpm web:freigabetexte`, `E-2162`).
 */
export function useVierAugen(): boolean {
  return useStand().vierAugen;
}
